const link = (href: string, text: string) => (
  <a href={href} target="_blank" rel="noreferrer noopener">{text}</a>
);

/** Plain-English prompts about the rules. General information, not advice. */
export function RulesPage() {
  return (
    <div className="page narrow">
      <h1>Know the rules</h1>
      <p className="lede">
        Flipping cars is a business, and the rules for businesses apply to you even if you do it part-time. This page is a
        starting point for UK readers. It is general information, not legal or tax advice. Rules change, so check the
        linked official pages.
      </p>

      <section className="card">
        <h2>1. It counts as trading</h2>
        <p>
          Buying cars with the aim of selling them for profit, especially more than once, is normally trading. You pay tax on the
          profit. You can deduct genuine costs (the car, parts, MOT, advert fees), which is why keeping receipts matters.
        </p>
        <p>
          The £1,000 trading allowance is measured on what you take in, not on profit. One car sold for £2,000 is already over it,
          so expect to register for Self Assessment. See {link('https://www.gov.uk/guidance/tax-free-allowances-on-property-and-trading-income', 'HMRC on the trading allowance')}.
        </p>
      </section>

      <section className="card">
        <h2>2. Selling as a trader</h2>
        <ul>
          <li>Consumers who buy from a trader have strong rights: the car must be as described, of satisfactory quality and fit for purpose.</li>
          <li>"Sold as seen" or "no returns" does not remove those rights when you are a trader.</li>
          <li>Posing as a private seller when you are really trading is against the law (it is called curbstoning).</li>
          <li>Describe faults honestly. It is the cheapest way to avoid refunds and disputes.</li>
        </ul>
        <p>{link('https://www.gov.uk/consumer-protection-rights', 'Consumer rights on gov.uk')}</p>
      </section>

      <section className="card">
        <h2>3. Insurance</h2>
        <p>
          Ordinary car insurance often does not cover buying and selling cars as a business, or driving a car you do not
          yet own. Ask an insurer about motor trade cover or the right policy before you collect a car. Driving without valid
          insurance is a serious offence.
        </p>
      </section>

      <section className="card">
        <h2>4. Tax, MOT and SORN</h2>
        <ul>
          <li>A car must be taxed, MOT'd (if it needs one) and insured to drive on the road. Vehicle tax does not pass to the buyer.</li>
          <li>If a car stays off the road, make a SORN declaration so you are not charged tax for it.</li>
          <li>Check a car's tax and MOT: {link('https://www.gov.uk/check-vehicle-tax', 'vehicle tax')}, {link('https://www.gov.uk/check-mot-history', 'MOT history')}.</li>
        </ul>
      </section>

      <section className="card">
        <h2>5. Paperwork with DVLA</h2>
        <p>
          When you buy, make sure the logbook (V5C) is in the seller's name and you get the new keeper slip. When you sell, tell
          DVLA straight away. {link('https://www.gov.uk/sell-car', 'Selling a car on gov.uk')}.
        </p>
      </section>

      <section className="card">
        <h2>6. If you claim benefits</h2>
        <p>
          Money from trading must be reported. On Universal Credit, earnings reduce your payment (by 55p for each £1 over any work
          allowance), and there are special rules for self-employed people. Talk to your work coach before you start, not after.
        </p>
      </section>

      <section className="card">
        <h2>7. Stay safe</h2>
        <ul>
          <li>Always run a history check. A car with outstanding finance can be taken back, and you lose the car and the money.</li>
          <li>Walk away from a seller who will not show you the V5C or who wants to meet somewhere odd.</li>
          <li>Never hand over a car until the payment has cleared in your bank. Be wary of overpayments and fake banking apps.</li>
          <li>Only put in cash you can afford to lose. The first few cars teach you, and some of the lessons cost money.</li>
        </ul>
      </section>
    </div>
  );
}
